/**
 * The on-device face engine: loading it, warming it, and running one photo.
 *
 * ── The whole design goal is that nobody waits ───────────────────────────────
 *
 * The engine is 12 MB of WASM plus a 3.8 MB model. Loaded when the visitor
 * presses the button, that is a progress bar between the intent and the result
 * — on the one screen where the intent is fragile, because the visitor is about
 * to hand a photograph of their own face to a website. So it is loaded on
 * ARRIVAL instead, during the seconds they spend reading the page and finding a
 * photo, and by the time they drop one in it is already warm.
 *
 * Three things make that work:
 *
 *  1. **One singleton promise per document.** Every tool page shares it, so
 *     moving from the symmetry tool to the jawline tool costs nothing — and
 *     with client-side routing that is the common path, not the edge case.
 *  2. **`<link rel="preload">` in the document head**, so the browser starts
 *     the two big fetches from the HTML parser rather than after React has
 *     hydrated. See `routes/tools.tsx`.
 *  3. **The HTTP cache does the second visit.** The asset URLs are stable, the
 *     Worker serves them immutable, and the second scan of the session — or the
 *     next day's visit — resolves without a network round trip.
 *
 * ── The rule this file exists to obey ────────────────────────────────────────
 *
 * House rule 6, and specifically its second half: an integration must not be
 * able to fail OR to hang. That rule was written after App Check's `getApp()`
 * threw synchronously past a `.catch()` and its `getToken()` never settled at
 * all, which left every request in the mobile app waiting on a header forever.
 *
 * The same two shapes are both present here. `FilesetResolver.forVisionTasks`
 * rejects if the WASM is missing — that one is easy. But a WASM instantiation
 * that stalls (a truncated response from a flaky CDN edge, a device that runs
 * out of memory mid-compile) does not reject; it simply never resolves, and the
 * page sits on "preparing" until the tab is closed. So every load is raced
 * against a timeout, and a failed engine degrades the page to its prose rather
 * than to a spinner.
 */

import type { FaceLandmarker } from "@mediapipe/tasks-vision";

/** Where `scripts/sync-face-assets.mjs` puts the binaries. */
const ASSET_BASE = "/face";
export const WASM_BASE = `${ASSET_BASE}`;
export const MODEL_URL = `${ASSET_BASE}/face_landmarker.task`;

/**
 * How long a load is allowed to take before it is called a failure.
 *
 * Thirty seconds is long enough for the whole 16 MB over a slow connection —
 * measured at roughly 11 s on a throttled "Fast 3G" profile — and short enough
 * that a visitor on a stalled load gets an explanation instead of an
 * indefinite spinner. The number matters less than the existence of a bound:
 * an unbounded wait is the failure mode the house rule names.
 */
const LOAD_TIMEOUT_MS = 30_000;

export type EngineStatus = "idle" | "loading" | "ready" | "failed";

export interface EngineFailure {
  /** Coarse reason, safe to send to analytics. Never carries a stack. */
  readonly reason: "timeout" | "unsupported" | "network" | "unknown";
  readonly message: string;
}

let landmarkerPromise: Promise<FaceLandmarker> | null = null;

/**
 * Had a load already been started — in flight or resolved — before this call?
 *
 * Not "resolved": a second tool page mounting mid-download is warm too, and it
 * really does wait for the rest of that download. That is why the load event's
 * `ms` is timed per call rather than globally; a warm mount's wait is a real
 * number, it is just usually near zero.
 */
export function isEngineWarm(): boolean {
  return landmarkerPromise !== null;
}

function classify(error: unknown): EngineFailure["reason"] {
  const message = error instanceof Error ? error.message : String(error);
  if (/timed out/i.test(message)) return "timeout";
  if (/WebAssembly|SIMD|not supported/i.test(message)) return "unsupported";
  if (/fetch|network|404|Failed to load/i.test(message)) return "network";
  return "unknown";
}

/**
 * Start loading the engine, or return the load already in flight.
 *
 * Safe to call from anywhere, any number of times, including during SSR — where
 * it resolves to a rejected promise that nothing awaits, because `document` is
 * the guard. Callers get the same promise, so N tool pages cause one download.
 */
export function loadFaceEngine(): Promise<FaceLandmarker> {
  if (landmarkerPromise) return landmarkerPromise;

  if (typeof window === "undefined") {
    // Never cached: a rejection cached here would poison the client's first
    // real attempt after hydration.
    return Promise.reject(new Error("face engine is browser-only"));
  }

  landmarkerPromise = (async () => {
    // Imported dynamically so the 300 KB of MediaPipe glue is a chunk the
    // landing page and the glossary never download. Tool pages pull it on
    // mount; everything else on the site never touches it.
    const { FaceLandmarker, FilesetResolver } = await import(
      "@mediapipe/tasks-vision"
    );

    const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
    return FaceLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URL,
        // GPU would be faster per frame and is the wrong trade here: these
        // tools measure ONE still image, so the per-run cost is irrelevant,
        // while GPU delegation adds a WebGL context that fails on exactly the
        // low-end and locked-down browsers this has to work on. CPU is the
        // path with no second way to fail.
        delegate: "CPU",
      },
      runningMode: "IMAGE",
      numFaces: 1,
      // Blendshapes and the transform matrix are for animation and head-pose;
      // nothing here reads them, and asking for them costs inference time on
      // every run.
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: false,
    });
  })();

  // The bound. Note it wraps the STORED promise rather than replacing it: the
  // underlying load is left to finish if it eventually does, so a visitor who
  // waits out a slow connection and tries again gets the warm engine.
  const bounded = withTimeout(landmarkerPromise, LOAD_TIMEOUT_MS);

  bounded.catch(() => {
    // Drop the cached promise so a retry is a real retry. Without this, one
    // failed load makes the tool permanently broken for the rest of the
    // session — which is how a transient network blip becomes a bug report.
    landmarkerPromise = null;
  });

  return bounded;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`face engine load timed out after ${ms}ms`)),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/*
 * There is deliberately no `loadElapsedMs()` here any more, and this note is
 * the reason it must not come back.
 *
 * It used to read a module-level `loadStartedAt` — the moment the FIRST load in
 * the document began. That is the right clock for exactly one caller: the mount
 * that started the load. Every other mount shares this singleton promise, so on
 * a warm mount the promise resolves in a microtask while the module clock keeps
 * counting from whenever the visitor arrived, and the event recorded how long
 * they had been on the site rather than how long anything took. Measured in
 * PostHog project "Fazly Web" (595575) over the 30 days to 2026-09-19: warm
 * rows reported a median of 163,419 ms and a p95 of 1,009,776 ms against
 * 805 ms / 5,735 ms for cold ones, and 646 of 1,500 rows were warm — so nearly
 * half the dataset was a session-length disguised as a load time, which is
 * worse than no number because it aggregates.
 *
 * A duration belongs to the CALL, not to the module, so each caller times its
 * own `loadFaceEngine()` across the await. See `FaceScanner`'s mount effect.
 */

export { classify as classifyEngineFailure };

/**
 * Decode a file the visitor chose into something the landmarker can read.
 *
 * `createImageBitmap` rather than an `<img>` with an object URL: it decodes off
 * the main thread, and it honours EXIF orientation when asked. That second part
 * is not cosmetic — a portrait photo from an iPhone is stored landscape with an
 * orientation tag, and a decoder that ignores the tag hands the landmarker a
 * face rotated 90°, which it simply fails to find. The symptom is "no face
 * detected" on a photo that obviously contains one, reported only by iPhone
 * users, which is a miserable thing to debug from a bug report.
 */
export async function decodeImage(
  file: File,
): Promise<{ bitmap: ImageBitmap; via: DecodePath }> {
  const direct: Attempt = [
    "direct",
    () => createImageBitmap(file, { imageOrientation: "from-image" }),
  ];
  const resized: Attempt = [
    "resized",
    () =>
      createImageBitmap(file, {
        imageOrientation: "from-image",
        // Width only: the spec derives the height from the aspect ratio, and
        // the orientation is applied first, so a portrait photo stays portrait.
        resizeWidth: MAX_EDGE,
        resizeQuality: "high",
      }),
  ];
  const element: Attempt = ["element", () => decodeViaElement(file)];

  const order =
    file.size > LARGE_FILE_BYTES
      ? [resized, direct, element]
      : [direct, resized, element];

  let last: unknown = new Error("image could not be decoded");
  for (const [via, attempt] of order) {
    try {
      return { bitmap: await attempt(), via };
    } catch (error) {
      last = error;
    }
  }
  throw last;
}

/**
 * Which decode path produced the bitmap, reported on the scan event.
 *
 * ── Why there are three ──────────────────────────────────────────────────────
 *
 * Until 2026-09 there was one, `createImageBitmap(file)`, and every decode
 * failure on the site was on Chrome — 76 of 76, none on any Safari. PostHog
 * split them into two clusters with different causes:
 *
 *   - **HEIC from Android.** 17 of 17 image/heic scans on Android Chrome
 *     failed. Samsung and Pixel "high efficiency" camera modes write HEIC,
 *     `accept="image/*"` admits it, and Chrome has no HEIC decoder. Safari
 *     does, which is why iOS never showed this. Nothing below fixes it — the
 *     honest fix is a message that names the format — but the chain still
 *     runs, because a device whose system decoder does handle HEIC will pass
 *     through the `<img>` path.
 *   - **Files over ~8 MB**, on Android Chrome, Windows and Mac alike: the
 *     full-resolution decode of a 50-megapixel photo can fail on memory before
 *     `toCanvas` ever gets to downscale it. Asking the decoder for a 1024 px
 *     output lets it decode at a reduced size — for JPEG, Chrome can skip most
 *     of the work — so large files try that FIRST.
 *
 * The `<img>` element is last because it is the slowest and decodes on the
 * main thread, so it is tried only once both bitmap paths have refused. It exists for
 * the files `createImageBitmap(Blob)` rejects that the page's own image decoder
 * accepts, which is a real set on Chrome (some progressive and CMYK JPEGs).
 *
 * Both failures were being reported to the visitor as "No face found in that
 * photo", which blamed the one thing that was not wrong. `FaceScanner` now
 * says it was the file.
 */
export type DecodePath = "direct" | "resized" | "element";

type Attempt = readonly [DecodePath, () => Promise<ImageBitmap>];

/** Above this, try the decode-time downscale before the full-size decode. */
const LARGE_FILE_BYTES = 8 * 1024 * 1024;

/**
 * Decode through an `<img>`, then copy into a bitmap.
 *
 * `img.decode()` rejects on an undecodable file rather than hanging, so this
 * cannot stall the scan. The element applies EXIF orientation by default (CSS
 * `image-orientation: from-image`), and so does `createImageBitmap` of an
 * element, so this path agrees with the other two about which way is up.
 */
async function decodeViaElement(file: File): Promise<ImageBitmap> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    return await createImageBitmap(img);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Whether a file is HEIC/HEIF, for choosing the sentence — never sent.
 *
 * The MIME type is usually set, and the extension is the fallback for the
 * pickers that leave `type` empty. The name is read here, on the device, to
 * pick a message; it is not an analytics property and must not become one.
 */
export function isHeic(file: File): boolean {
  return (
    /^image\/hei[cf]/i.test(file.type) || /\.(heic|heif)$/i.test(file.name)
  );
}

/** The largest edge we feed the model, in pixels. */
const MAX_EDGE = 1024;

/**
 * Draw a bitmap onto a canvas, downscaled so the long edge is at most
 * `MAX_EDGE`.
 *
 * The landmarker's own input is 192×192, so anything above about a thousand
 * pixels is detail thrown away — but it is thrown away AFTER being copied into
 * WASM memory, and a modern phone photo is 4032 px wide. Downscaling first took
 * a 12 MP photo's end-to-end time from roughly 900 ms to 180 ms on a mid-range
 * Android, and on a low-memory device it is the difference between a result and
 * a tab that reloads itself.
 *
 * The canvas is returned rather than the bitmap because the measurements need
 * the pixel dimensions the landmarks are normalized against, and after a
 * downscale those are the canvas's, not the file's.
 */
export function toCanvas(bitmap: ImageBitmap): HTMLCanvasElement {
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("2d canvas is unavailable");
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas;
}
